import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout';
import ArticleHeader from '@/components/story/ArticleHeader';
import AuthorInfo from '@/components/story/AuthorInfo';
import ArticleContent from '@/components/story/ArticleContent';
import CommentSection from '@/components/story/CommentSection';
import StorySidebar from '@/components/story/StorySidebar';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/contexts/ThemeContext';
import contentfulService from '@/services/contentfulService';
import { Skeleton } from '@/components/ui/skeleton';
import VimeoPlayer from '@/components/vimeo/VimeoPlayer';
import { useIsMobile } from '@/hooks/use-mobile';
import { extractImageUrlsFromMarkdown, validateImageUrl } from '@/utils/imageUtils';

const StoryDetail = () => {
  // ALL HOOKS MUST COME FIRST - BEFORE ANY CONDITIONAL LOGIC
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    resolvedTheme
  } = useTheme();
  const isMobile = useIsMobile();
  const {
    data: story,
    isLoading,
    error
  } = useQuery({
    queryKey: ['story', slug],
    queryFn: () => slug ? contentfulService.getStoryBySlug(slug) : null,
    enabled: !!slug
  });
  const {
    data: allStories = []
  } = useQuery({
    queryKey: ['stories'],
    queryFn: contentfulService.getStories
  });

  // ALL useMemo and derived values
  const allImages = useMemo(() => {
    if (!story?.content || !story?.coverImage) return [];
    try {
      const contentImages = extractImageUrlsFromMarkdown(story.content).map(url => validateImageUrl(url)).filter(url => url !== '');
      const combinedImages = [story.coverImage, ...contentImages];
      return combinedImages.filter((img, index, arr) => arr.indexOf(img) === index);
    } catch (error) {
      console.error('Error extracting images:', error);
      return story.coverImage ? [story.coverImage] : [];
    }
  }, [story?.content, story?.coverImage]);
  const relatedStories = useMemo(() => {
    if (!story || !allStories.length) return [];
    return allStories.filter(s => s.id !== story.id && (s.category === story.category || Array.isArray(s.tags) && Array.isArray(story.tags) && s.tags.some(tag => story.tags.includes(tag)))).slice(0, 3);
  }, [story, allStories]);
  const formattedDate = useMemo(() => {
    if (!story?.publishedAt) return '';
    return new Date(story.publishedAt).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }, [story?.publishedAt]);

  // ALL useEffect hooks
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  useEffect(() => {
    if (story) {
      console.log('StoryDetail - Story loaded:', story.title);
      console.log('StoryDetail - Content preview:', story.content?.substring(0, 100) + '...');
      console.log('StoryDetail - Tags:', story.tags);
      console.log('StoryDetail - Raw tags:', story.tags);
      if (story.vimeo) {
        console.log('StoryDetail - Vimeo ID:', story.vimeo);
      }
    }
  }, [story]);

  // NOW WE CAN HAVE CONDITIONAL RETURNS - ALL HOOKS HAVE BEEN CALLED
  if (isLoading) {
    return <Layout>
        <div className="w-full h-[60vh]">
          <Skeleton className="w-full h-full" />
        </div>
        <div className="article-container py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-7 md:col-start-2">
              <Skeleton className="h-8 w-full mb-3" />
              <Skeleton className="h-6 w-2/3 mb-8" />
              <Skeleton className="h-[400px] w-full mb-6" />
              <Skeleton className="h-6 w-full mb-3" />
              <Skeleton className="h-6 w-full mb-3" />
              <Skeleton className="h-6 w-4/5 mb-8" />
            </div>
            <div className="md:col-span-3">
              <Skeleton className="h-[300px] w-full" />
            </div>
          </div>
        </div>
      </Layout>;
  }
  if (error || !story) {
    return <Layout>
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-3xl font-normal mb-4 font-chroma">Story Not Found</h1>
          <p className="text-gray-400 mb-6 font-ibm">The story you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/stories')}>
            Back to Stories
          </Button>
        </div>
      </Layout>;
  }
  return <Layout>
      <Helmet>
        <title>{story.title} - Everhouse</title>
        <meta name="description" content={story.excerpt || story.subtitle || story.title} />
        
        {/* Open Graph meta tags */}
        <meta property="og:title" content={story.title} />
        <meta property="og:description" content={story.excerpt || story.subtitle || story.title} />
        <meta property="og:image" content={story.coverImage ? story.coverImage : 'https://everhouse.co/lovable-uploads/45eb7b3f-a007-4737-99ac-c4833cfe0878.png'} />
        <meta property="og:image:secure_url" content={story.coverImage ? story.coverImage : 'https://everhouse.co/lovable-uploads/45eb7b3f-a007-4737-99ac-c4833cfe0878.png'} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Everhouse" />
        
        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={story.title} />
        <meta name="twitter:description" content={story.excerpt || story.subtitle || story.title} />
        <meta name="twitter:image" content={story.coverImage || 'https://everhouse.co/lovable-uploads/45eb7b3f-a007-4737-99ac-c4833cfe0878.png'} />
        <meta name="twitter:url" content={window.location.href} />
        
        {/* Additional meta tags */}
        <meta name="author" content={story.author?.name || 'Everhouse'} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <div className="w-full overflow-hidden">
        <ArticleHeader title={story.title} subtitle={story.subtitle} category={story.category} readTime={story.readTime} coverImage={story.coverImage} images={allImages} />

        <div className={`${resolvedTheme === 'dark' ? 'bg-netflix-black' : 'bg-background'} w-full overflow-hidden`}>
          <div className="article-container md:pb-16 overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8">
              <div className={`md:col-span-7 md:col-start-2 ${isMobile ? '' : 'px-2'} overflow-hidden -mt-6 md:mt-0 mb-0 pb-0`}>
                <AuthorInfo author={story.author} publishedDate={formattedDate} />
                
                {story.vimeo && <div className="w-full max-w-full my-6 overflow-hidden">
                    <VimeoPlayer vimeoId={story.vimeo} title={story.title} minHeight="auto" responsive={true} className="rounded-none md:rounded-md overflow-hidden" />
                  </div>}
                
                <ArticleContent subtitle={story.subtitle} content={story.content} tags={story.tags} author={story.author} relatedStories={relatedStories} />

                <CommentSection storyId={story.id} comments={story.comments} />
              </div>

              <div className="md:col-span-3 md:mt-16">
                <StorySidebar currentStory={story} relatedStories={relatedStories} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>;
};
export default StoryDetail;